import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useSectionContent } from '../hooks/useSiteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ArrowRight, Building2, MapPin, Truck } from 'lucide-react';

const ABOUT_DEFAULTS = {
  about_hero_title: 'About BlueLock Freights',
  about_hero_subtitle: 'Michigan-based motor carrier built around driver partnerships.',
  about_who_we_are:
    'BlueLock Freights operates as a trade/brand name associated with BlueLock Transport LLC, headquartered at 4370 Chicago Drive SW, Grandville, MI 49418. We operate under USDOT 4557330 and MC 1810883.',
  about_what_we_do:
    'We coordinate freight for Owner-Operators, Lease-On Drivers, and Company Drivers across OTR and regional lanes, with a focus on consistent dispatch, documented settlements, and regulatory compliance.',
  about_our_approach:
    'We invest in long-term driver partnerships through clear written agreements, transparent settlement practices, and accessible operations support - including after-hours dispatch coverage.',
};

const AboutPage = () => {
  const { content } = useSectionContent('about', ABOUT_DEFAULTS);
  useDocumentTitle({
    title: 'About BlueLock Freights | Michigan Motor Carrier',
    description:
      content.about_hero_subtitle ||
      'About BlueLock Freights — a trade/brand name associated with BlueLock Transport LLC, USDOT 4557330 / MC 1810883.',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            data-testid="about-hero-title"
          >
            {content.about_hero_title}
          </h1>
          <p
            className="text-lg sm:text-xl text-blue-100 max-w-3xl"
            data-testid="about-hero-subtitle"
          >
            {content.about_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div data-testid="about-who-we-are">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-7 h-7 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900">Who We Are</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {content.about_who_we_are}
            </p>
          </div>

          <div data-testid="about-what-we-do">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="w-7 h-7 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900">What We Do</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {content.about_what_we_do}
            </p>
          </div>

          <div data-testid="about-our-approach">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-7 h-7 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900">Our Approach</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {content.about_our_approach}
            </p>
          </div>

          <div className="pt-6 border-t border-gray-200 flex flex-wrap gap-4">
            <Link
              to="/programs"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700"
              data-testid="about-link-programs"
            >
              See our driver programs <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700"
              data-testid="about-link-contact"
            >
              Contact us <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutPage;
